import { useEffect, useState } from "react";
import axios from "axios";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { VehicleCard } from "@/components/VehicleCard";
import { TextToRentCTA, TextToRentBanner } from "@/components/TextToRentCTA";
import { DollarSign, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const FleetPage = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("newest");

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const response = await axios.get(`${API}/vehicles`);
        setVehicles(response.data);
      } catch (error) {
        console.error("Error fetching vehicles:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVehicles();
  }, []);

  const sortedVehicles = [...vehicles].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return b.year - a.year;
      case "oldest":
        return a.year - b.year;
      case "make":
        return a.make.localeCompare(b.make);
      default:
        return 0;
    }
  });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="fleet-page">
      <Header />

      {/* Hero */}
      <section className="bg-slate-900 text-white py-16 md:py-24">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 bg-amber-400/20 px-4 py-2 mb-4">
              <DollarSign className="w-4 h-4 text-amber-400" />
              <span className="text-amber-400 text-sm font-medium uppercase tracking-wide">
                All vehicles $200/week
              </span>
            </span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white mb-4">
              Our Fleet
            </h1>
            <p className="text-slate-400 text-lg">
              Browse our available vehicles. Perfect for Grub Hub, Uber Eats, Instacart, 
              or your daily work commute. No credit checks. No deposit. Unlimited in-town mileage.
            </p>
          </div>
        </div>
      </section>

      {/* Filter Bar */}
      <section className="bg-white border-b border-slate-200 py-4">
        <div className="container-custom px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between">
            <p className="text-slate-600 text-sm">
              <span className="font-bold text-slate-900">{vehicles.length}</span> vehicles available
            </p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="sort-dropdown">
                  <Filter className="w-4 h-4" />
                  Sort by
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setSortBy("newest")} data-testid="sort-newest">
                  Newest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("oldest")} data-testid="sort-oldest">
                  Oldest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy("make")} data-testid="sort-make">
                  By Make
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </section>

      {/* Vehicle Grid */}
      <section className="section-padding">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="bg-white aspect-[4/3] animate-pulse border border-slate-200"></div>
              ))}
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-slate-500 text-lg">No vehicles available at the moment.</p>
              <p className="text-slate-400 text-sm mt-2">
                Please check back later or text us for availability.
              </p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
              {sortedVehicles.map((vehicle, index) => (
                <VehicleCard key={vehicle.id} vehicle={vehicle} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      <TextToRentBanner />
      <Footer />
      <TextToRentCTA />
    </div>
  );
};

export default FleetPage;
